import { Toggle } from "../../components/Toggle";
import { t } from "../../i18n";
import { useCallback, useEffect, useState } from "react";
import {
  CircleCheck,
  CircleX,
  FlaskConical,
  Link2,
  LogIn,
  Plus,
  Save,
  ServerCog,
  Trash2,
} from "lucide-react";
import type { McpServerDefinition, McpTestResult } from "@lumen/shared";
import { api } from "../../api";
import { completePersonalizationRequestFromLocation } from "../../personalization";
import { SettingsShell } from "../../components/settings/SettingsShell";
import { McpBasicFields } from "../../components/integrations/McpEditor";
type McpDraft = {
  name: string;
  toolPrefix: string;
  description: string;
  transport: "streamable-http" | "sse";
  endpointUrl: string;
  authType: McpServerDefinition["authType"];
  authScope: McpServerDefinition["authScope"];
  config: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthScopes: string;
  oauthAuthorizationServerUrl: string;
  oauthAuthorizationEndpoint: string;
  oauthTokenEndpoint: string;
  oauthTokenEndpointAuthMethod:
    "none" | "client_secret_post" | "client_secret_basic";
  enabled: boolean;
  scope: "platform" | "organization" | "personal";
};
const emptyMcp: McpDraft = {
  name: "",
  toolPrefix: "",
  description: "",
  transport: "streamable-http",
  endpointUrl: "",
  authType: "none",
  authScope: "global",
  config: "",
  oauthClientId: "",
  oauthClientSecret: "",
  oauthScopes: "",
  oauthAuthorizationServerUrl: "",
  oauthAuthorizationEndpoint: "",
  oauthTokenEndpoint: "",
  oauthTokenEndpointAuthMethod: "none",
  enabled: true,
  scope: "personal",
};

type McpShortcut = {
  id: string;
  name: string;
  description: string;
  endpointUrl: string;
  authType?: "oauth" | "bearer";
};

const mcpShortcuts: McpShortcut[] = [
  {
    id: "github",
    name: "GitHub MCP",
    description:
      "Repositories, pull requests, Actions, code security, and Dependabot.",
    endpointUrl: "https://api.githubcopilot.com/mcp/",
  },
  {
    id: "gitlab",
    name: "GitLab MCP",
    description: "GitLab Orbit project and graph tools.",
    endpointUrl: "https://gitlab.com/api/v4/orbit/mcp",
  },
  {
    id: "buildkite",
    name: "Buildkite MCP",
    description: "Pipelines, builds, jobs, logs, and test data.",
    endpointUrl: "https://mcp.buildkite.com/mcp",
  },
  {
    id: "circleci",
    name: "CircleCI MCP",
    description: "Pipeline failures, logs, workflows, and reruns.",
    endpointUrl: "https://mcp.circleci.com/v1/mcp",
  },
  {
    id: "cloudflare",
    name: "Cloudflare MCP",
    description:
      "Cloudflare’s API, Workers, DNS, storage, and analytics tools.",
    endpointUrl: "https://mcp.cloudflare.com/mcp",
  },
  {
    id: "vercel",
    name: "Vercel MCP",
    description: "Projects, deployments, logs, and Vercel documentation.",
    endpointUrl: "https://mcp.vercel.com",
  },
  {
    id: "sentry",
    name: "Sentry MCP",
    description: "Issues, traces, performance, and debugging context.",
    endpointUrl: "https://mcp.sentry.dev/mcp",
  },
  {
    id: "mollie",
    name: "Mollie MCP",
    description:
      "Mollie payments, invoices, customers, settlements, terminals, and webhooks.",
    endpointUrl: "https://mcp.mollie.com/mcp",
    authType: "bearer",
  },
  {
    id: "stripe",
    name: "Stripe MCP",
    description:
      "Stripe payments, customers, invoices, subscriptions, disputes, and documentation.",
    endpointUrl: "https://mcp.stripe.com",
    authType: "oauth",
  },
];

function mcpConfigPlaceholder(draft: McpDraft, selected: boolean) {
  if (selected) return "Leave blank to keep the encrypted configuration";
  if (draft.authType === "bearer") return '{\n  "token": "…"\n}';
  if (draft.authType === "api-key")
    return '{\n  "apiKey": "…",\n  "headerName": "x-api-key"\n}';
  if (draft.authType === "custom-headers")
    return '{\n  "headers": {\n    "Authorization": "…"\n  }\n}';
  return "{}";
}

export function McpPage({
  canManage,
  canManagePlatform = false,
  organizationOnly = false,
}: {
  canManage: boolean;
  canManagePlatform?: boolean;
  organizationOnly?: boolean;
}) {
  const [rows, setRows] = useState<McpServerDefinition[]>([]);
  const [selected, setSelected] = useState<string>();
  const [draft, setDraft] = useState<McpDraft>(() => ({
    ...emptyMcp,
    scope: organizationOnly
      ? "organization"
      : canManagePlatform
        ? "platform"
        : "personal",
  }));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<McpTestResult | null>(null);
  const validToolPrefix = /^[a-zA-Z0-9_-]{0,32}$/.test(draft.toolPrefix.trim());
  const load = useCallback(
    () =>
      api<McpServerDefinition[]>("/mcp-servers")
        .then((nextRows) =>
          setRows(
            organizationOnly
              ? nextRows.filter((row) => row.scope === "organization")
              : nextRows,
          ),
        )
        .catch((reason) => setError(reason.message)),
    [organizationOnly],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const choose = (row: McpServerDefinition) => {
    const oauth = row.oauthConfig;
    setSelected(row.id);
    setDraft({
      name: row.name,
      toolPrefix: row.toolPrefix ?? "",
      description: row.description,
      transport: row.transport === "stdio" ? "streamable-http" : row.transport,
      endpointUrl: row.endpointUrl,
      authType: row.authType,
      authScope: row.authScope,
      config: "",
      oauthClientId: oauth?.clientId ?? "",
      oauthClientSecret: "",
      oauthScopes: oauth?.scopes ?? "",
      oauthAuthorizationServerUrl: oauth?.authorizationServerUrl ?? "",
      oauthAuthorizationEndpoint: oauth?.authorizationEndpoint ?? "",
      oauthTokenEndpoint: oauth?.tokenEndpoint ?? "",
      oauthTokenEndpointAuthMethod: oauth?.tokenEndpointAuthMethod ?? "none",
      enabled: row.enabled,
      scope: row.scope,
    });
    setError("");
    setTestResult(null);
  };
  const fresh = () => {
    setSelected(undefined);
    setDraft({
      ...emptyMcp,
      scope: organizationOnly
        ? "organization"
        : canManagePlatform
          ? "platform"
          : "personal",
    });
    setError("");
    setTestResult(null);
  };
  const addShortcut = async (shortcut: McpShortcut) => {
    const existing = rows.find(
      (row) => row.endpointUrl === shortcut.endpointUrl,
    );
    if (existing) {
      choose(existing);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const created = await api<McpServerDefinition>("/mcp-servers", {
        method: "POST",
        body: JSON.stringify({
          name: shortcut.name,
          description: shortcut.description,
          transport: "streamable-http",
          endpointUrl: shortcut.endpointUrl,
          authType: shortcut.authType ?? "oauth",
          authScope: "user",
          scope: organizationOnly
            ? "organization"
            : canManagePlatform
              ? "platform"
              : "personal",
          enabled: true,
        }),
      });
      await load();
      choose(created);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not add MCP shortcut",
      );
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      let config: Record<string, unknown> | undefined;
      if (draft.authType === "oauth")
        config = {
          clientId: draft.oauthClientId.trim(),
          scopes: draft.oauthScopes.trim(),
          authorizationServerUrl: draft.oauthAuthorizationServerUrl.trim(),
          authorizationEndpoint: draft.oauthAuthorizationEndpoint.trim(),
          tokenEndpoint: draft.oauthTokenEndpoint.trim(),
          tokenEndpointAuthMethod: draft.oauthTokenEndpointAuthMethod,
          ...(draft.oauthClientSecret
            ? { clientSecret: draft.oauthClientSecret }
            : {}),
        };
      else if (draft.config.trim()) config = JSON.parse(draft.config);
      const {
        oauthClientId,
        oauthClientSecret,
        oauthScopes,
        oauthAuthorizationServerUrl,
        oauthAuthorizationEndpoint,
        oauthTokenEndpoint,
        oauthTokenEndpointAuthMethod,
        ...values
      } = draft;
      await api<McpServerDefinition>(
        selected ? `/mcp-servers/${selected}` : "/mcp-servers",
        {
          method: selected ? "PATCH" : "POST",
          body: JSON.stringify({
            ...values,
            scope: organizationOnly ? "organization" : values.scope,
            config,
          }),
        },
      );
      await load();
      if (!selected) fresh();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save MCP server",
      );
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api(`/mcp-servers/${selected}`, { method: "DELETE" });
      await completePersonalizationRequestFromLocation({
        resourceType: "mcp_server",
        resourceId: selected,
      }).catch(() => undefined);
      fresh();
      await load();
    } finally {
      setSaving(false);
    }
  };
  const saveMyCredentials = async () => {
    if (!selected || !draft.config.trim()) return;
    setSaving(true);
    setError("");
    try {
      const config =
        draft.authType === "bearer"
          ? { token: draft.config.trim() }
          : draft.authType === "api-key"
            ? { apiKey: draft.config.trim() }
            : { headers: JSON.parse(draft.config) };
      await api(`/mcp-servers/${selected}/credentials`, {
        method: "PUT",
        body: JSON.stringify({ config }),
      });
      await completePersonalizationRequestFromLocation({
        resourceType: "mcp_server",
        resourceId: selected,
      }).catch(() => undefined);
      setDraft({ ...draft, config: "" });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save credentials",
      );
    } finally {
      setSaving(false);
    }
  };
  const test = async () => {
    if (!selected) return;
    setTesting(true);
    setError("");
    setTestResult(null);
    try {
      const result = await api<McpTestResult>(`/mcp-servers/${selected}/test`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setTestResult(result);
      if (result.ok)
        await completePersonalizationRequestFromLocation({
          resourceType: "mcp_server",
          resourceId: selected,
        }).catch(() => undefined);
    } catch (reason) {
      setTestResult({
        ok: false,
        message: reason instanceof Error ? reason.message : "MCP test failed",
      });
    } finally {
      setTesting(false);
    }
  };
  const connect = async () => {
    if (!selected) return;
    setError("");
    try {
      const result = await api<{ authorizationUrl: string }>(
        `/mcp-servers/${selected}/oauth/start`,
        {
          method: "POST",
          body: JSON.stringify({
            returnTo: window.location.pathname + window.location.search,
          }),
        },
      );
      window.location.assign(result.authorizationUrl);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not start OAuth",
      );
    }
  };
  const selectedRow = rows.find((row) => row.id === selected);
  const fillHomeAssistantEndpoints = () => {
    try {
      const origin = new URL(draft.endpointUrl).origin;
      setDraft({
        ...draft,
        oauthAuthorizationServerUrl: origin,
        oauthAuthorizationEndpoint: `${origin}/auth/authorize`,
        oauthTokenEndpoint: `${origin}/auth/token`,
        oauthTokenEndpointAuthMethod: "client_secret_post",
      });
    } catch {
      setError("Enter a valid MCP endpoint URL first.");
    }
  };
  return (
    <SettingsShell
      icon={ServerCog}
      title={t("MCP servers")}
      description="Server-side MCP connection definitions and message-level availability."
    >
      <div className="mb-4 rounded-xl border border-lumen-200 bg-lumen-50 p-3 text-xs leading-5 text-lumen-800 dark:border-lumen-800 dark:bg-lumen-900/30 dark:text-lumen-200">
        {t(
          "Enabled MCP functions are exposed to tool-capable models and executed by the API using each user’s configured credentials.",
        )}
      </div>
      {canManage && (
        <div className="mb-5 rounded-2xl border border-stone-200 p-4 dark:border-white/10">
          <div className="mb-3 flex items-center gap-2">
            <Link2 size={16} className="text-lumen-600" />
            <div>
              <h3 className="text-sm font-semibold">
                {t("Official MCP shortcuts")}
              </h3>
              <p className="text-xs text-stone-500">
                {t(
                  "Add a provider’s existing MCP server without duplicating it as a\n                native integration.",
                )}
              </p>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {mcpShortcuts.map((shortcut) => {
              const alreadyAdded = rows.some(
                (row) => row.endpointUrl === shortcut.endpointUrl,
              );
              return (
                <button
                  key={shortcut.id}
                  type="button"
                  className="rounded-xl border border-stone-200 p-3 text-left transition hover:border-lumen-400 hover:bg-lumen-50 dark:border-white/10 dark:hover:bg-lumen-950/30"
                  onClick={() => void addShortcut(shortcut)}
                  disabled={saving}
                >
                  <div className="flex items-center justify-between gap-2">
                    <strong className="text-sm">{shortcut.name}</strong>
                    <span className="text-[10px] font-semibold tracking-wide text-lumen-700 uppercase dark:text-lumen-300">
                      {alreadyAdded ? "Added" : "Add"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-stone-500">
                    {shortcut.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
        <div className="panel p-2">
          {canManage && (
            <button className="button mb-2 w-full" onClick={fresh}>
              <Plus size={16} /> {t("New MCP server")}
            </button>
          )}
          {rows.map((row) => (
            <button
              key={row.id}
              onClick={() => choose(row)}
              className={`w-full rounded-xl p-3 text-left ${selected === row.id ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
            >
              <div className="flex justify-between gap-2">
                <strong className="truncate text-sm">{row.name}</strong>
                <span className="text-[10px] text-stone-400 uppercase">
                  {row.scope}
                </span>
              </div>
              <p className="mt-1 truncate text-xs text-stone-500">
                {row.endpointUrl}
              </p>
              <p className="mt-1 text-[10px] tracking-wide text-stone-400 uppercase">
                {row.authType === "none" ? "No auth" : row.authType} ·{" "}
                {row.authScope === "global"
                  ? "workspace credential"
                  : "per user"}
              </p>
            </button>
          ))}
        </div>
        <div className="panel space-y-4 p-5">
          {!selected && !canManage ? (
            <div className="py-12 text-center text-sm text-stone-400">
              {t(
                "Select an MCP server to view it and configure your credentials.",
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <McpBasicFields
                  value={{ name: draft.name, description: draft.description }}
                  onChange={(value) => setDraft({ ...draft, ...value })}
                  disabled={!canManage}
                />
                <label>
                  <span id="mcp-tool-prefix-label" className="label">
                    {t("Tool prefix")}
                  </span>
                  <input
                    className="field"
                    value={draft.toolPrefix}
                    placeholder={t("Optional")}
                    disabled={!canManage}
                    maxLength={32}
                    aria-labelledby="mcp-tool-prefix-label"
                    aria-invalid={!validToolPrefix}
                    aria-describedby="mcp-tool-prefix-help"
                    onChange={(event) =>
                      setDraft({ ...draft, toolPrefix: event.target.value })
                    }
                  />
                  <span
                    id="mcp-tool-prefix-help"
                    className="mt-1 block text-xs text-stone-500 dark:text-stone-400"
                  >
                    {validToolPrefix
                      ? t(
                          "Use letters, numbers, underscores, or hyphens. Leave blank to use the server ID.",
                        )
                      : t(
                          "Tool prefix must contain only letters, numbers, underscores, or hyphens (up to 32 characters).",
                        )}
                  </span>
                  <span className="mt-1 block text-xs break-all text-stone-500 dark:text-stone-400">
                    {t("Tool name preview")}:{" "}
                    <code>
                      {`mcp_${draft.toolPrefix.trim() || selected?.replaceAll("-", "").slice(0, 8) || "server_id"}_tool_name`}
                    </code>
                  </span>
                </label>
                <label>
                  <span className="label">{t("Transport")}</span>
                  <select
                    disabled={!canManage}
                    className="field"
                    value={draft.transport}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        transport: event.target.value as McpDraft["transport"],
                      })
                    }
                  >
                    <option value="streamable-http">
                      {t("Streamable HTTP")}
                    </option>
                    <option value="sse">{t("SSE (legacy)")}</option>
                  </select>
                </label>
              </div>
              <label>
                <span className="label">{t("Endpoint URL")}</span>
                <input
                  disabled={!canManage}
                  type="url"
                  className="field"
                  placeholder="https://mcp.example.test/mcp"
                  value={draft.endpointUrl}
                  onChange={(event) =>
                    setDraft({ ...draft, endpointUrl: event.target.value })
                  }
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label>
                  <span className="label">{t("Authentication")}</span>
                  <select
                    disabled={!canManage}
                    className="field"
                    value={draft.authType}
                    onChange={(event) => {
                      const authType = event.target
                        .value as McpDraft["authType"];
                      setDraft({
                        ...draft,
                        authType,
                        authScope:
                          authType === "oauth" ? "user" : draft.authScope,
                      });
                    }}
                  >
                    <option value="none">{t("No authentication")}</option>
                    <option value="oauth">
                      {t("OAuth 2.1 (browser sign-in)")}
                    </option>
                    <option value="bearer">{t("Bearer token")}</option>
                    <option value="api-key">{t("API key")}</option>
                    <option value="custom-headers">
                      {t("Custom headers")}
                    </option>
                  </select>
                </label>
                <label>
                  <span className="label">{t("Credential ownership")}</span>
                  <select
                    className="field"
                    value={draft.authScope}
                    disabled={
                      !canManage ||
                      draft.authType === "none" ||
                      draft.authType === "oauth"
                    }
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        authScope: event.target.value as McpDraft["authScope"],
                      })
                    }
                  >
                    <option value="platform">
                      {t("One workspace credential")}
                    </option>
                    <option value="user">
                      {t("Each user sets up their own")}
                    </option>
                  </select>
                </label>
              </div>
              {draft.authType === "oauth" &&
                (canManage ? (
                  <div className="space-y-4 rounded-xl border border-stone-200 p-4 dark:border-white/10">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h3 className="text-sm font-semibold">
                          {t("OAuth client")}
                        </h3>
                        <p className="mt-1 text-xs text-stone-400">
                          {t(
                            "Tokens are stored separately for each user. Manual endpoints below are optional overrides.",
                          )}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="button text-xs"
                        onClick={fillHomeAssistantEndpoints}
                      >
                        {t("Use Home Assistant defaults")}
                      </button>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label>
                        <span className="label">{t("OAuth client ID")}</span>
                        <input
                          className="field"
                          placeholder="https://your-lumen.example"
                          value={draft.oauthClientId}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              oauthClientId: event.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        <span className="label">
                          {t("OAuth client secret")}
                        </span>
                        <input
                          type="password"
                          className="field"
                          placeholder={
                            selectedRow?.oauthConfig?.clientSecretConfigured
                              ? t("Leave blank to keep existing secret")
                              : t("Optional")
                          }
                          value={draft.oauthClientSecret}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              oauthClientSecret: event.target.value,
                            })
                          }
                        />
                      </label>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label>
                        <span className="label">{t("Scopes")}</span>
                        <input
                          className="field"
                          placeholder={t("Optional, space separated")}
                          value={draft.oauthScopes}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              oauthScopes: event.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        <span className="label">
                          {t("Token endpoint auth method")}
                        </span>
                        <select
                          className="field"
                          value={draft.oauthTokenEndpointAuthMethod}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              oauthTokenEndpointAuthMethod: event.target
                                .value as McpDraft["oauthTokenEndpointAuthMethod"],
                            })
                          }
                        >
                          <option value="none">
                            {t("None / public client")}
                          </option>
                          <option value="client_secret_post">
                            {t("Client secret in request body")}
                          </option>
                          <option value="client_secret_basic">
                            {t("HTTP Basic authentication")}
                          </option>
                        </select>
                      </label>
                    </div>
                    <label>
                      <span className="label">
                        {t("Authorization server base URL")}
                      </span>
                      <input
                        type="url"
                        className="field"
                        placeholder={t(
                          "Optional — use to discover endpoints from a known OAuth server",
                        )}
                        value={draft.oauthAuthorizationServerUrl}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            oauthAuthorizationServerUrl: event.target.value,
                          })
                        }
                      />
                    </label>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label>
                        <span className="label">{t("Authorization URL")}</span>
                        <input
                          type="url"
                          className="field"
                          placeholder={t(
                            "Optional — for example https://ha.example/auth/authorize",
                          )}
                          value={draft.oauthAuthorizationEndpoint}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              oauthAuthorizationEndpoint: event.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        <span className="label">{t("Token URL")}</span>
                        <input
                          type="url"
                          className="field"
                          placeholder={t(
                            "Optional — for example https://ha.example/auth/token",
                          )}
                          value={draft.oauthTokenEndpoint}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              oauthTokenEndpoint: event.target.value,
                            })
                          }
                        />
                      </label>
                    </div>
                    {selectedRow?.oauthConfig?.callbackUrl && (
                      <label>
                        <span className="label">{t("OAuth callback URL")}</span>
                        <input
                          readOnly
                          className="field font-mono text-xs text-stone-500"
                          value={selectedRow.oauthConfig.callbackUrl}
                        />
                        <span className="mt-1 block text-xs text-stone-400">
                          {t(
                            "Register this redirect URL if the authorization server requires pre-registration.",
                          )}
                        </span>
                      </label>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border border-stone-200 p-4 text-sm dark:border-white/10">
                    <p className="font-semibold">
                      {t("OAuth uses your own account")}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-stone-400">
                      {t(
                        "Connect below to authorize this MCP server. Your tokens are encrypted and stored separately from other users.",
                      )}
                    </p>
                  </div>
                ))}
              {canManage &&
                draft.authType !== "none" &&
                draft.authType !== "oauth" && (
                  <label>
                    <span className="label">
                      {draft.authScope === "global"
                        ? "Workspace credential (JSON)"
                        : "Your credential (JSON)"}
                    </span>
                    <textarea
                      className="field min-h-28 font-mono"
                      placeholder={mcpConfigPlaceholder(
                        draft,
                        Boolean(selected),
                      )}
                      value={draft.config}
                      onChange={(event) =>
                        setDraft({ ...draft, config: event.target.value })
                      }
                    />
                    <span className="mt-1 block text-xs text-stone-400">
                      {draft.authScope === "user"
                        ? "This value configures only your account; other users will be prompted in chat."
                        : "This encrypted credential is used for everyone with access to the MCP."}
                    </span>
                  </label>
                )}
              {!canManage &&
                draft.authScope === "user" &&
                draft.authType !== "none" &&
                draft.authType !== "oauth" && (
                  <label>
                    <span className="label">
                      {draft.authType === "bearer"
                        ? "Your bearer token"
                        : draft.authType === "api-key"
                          ? "Your API key"
                          : "Your custom headers (JSON)"}
                    </span>
                    <textarea
                      className="field min-h-24 font-mono"
                      placeholder={
                        draft.authType === "custom-headers"
                          ? '{\n  "Authorization": "Bearer …"\n}'
                          : selectedRow?.userConfigConfigured
                            ? t("Enter a replacement credential")
                            : t("Enter your credential")
                      }
                      value={draft.config}
                      onChange={(event) =>
                        setDraft({ ...draft, config: event.target.value })
                      }
                    />
                    <span className="mt-1 block text-xs text-stone-400">
                      {t("Saved only for your account and encrypted at rest.")}
                    </span>
                  </label>
                )}
              {selected && (
                <div className="rounded-xl border border-stone-200 p-3 dark:border-white/10">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      className="button"
                      disabled={testing}
                      onClick={() => void test()}
                    >
                      <FlaskConical size={16} />
                      {testing ? "Testing…" : "Test connection"}
                    </button>
                    {selectedRow?.authType === "oauth" && (
                      <button className="button" onClick={() => void connect()}>
                        <LogIn size={16} />
                        {selectedRow.userConfigConfigured
                          ? "Reconnect OAuth"
                          : "Connect OAuth"}
                      </button>
                    )}
                    <span className="ml-auto text-xs text-stone-400">
                      {selectedRow?.authType === "none"
                        ? "No credentials required"
                        : selectedRow?.userConfigConfigured ||
                            selectedRow?.configConfigured
                          ? "Credentials configured"
                          : "Credentials not configured"}
                    </span>
                  </div>
                  <p className="mt-2 text-[10px] text-stone-400">
                    {t("Connection actions use the last saved settings.")}
                  </p>
                  {testResult && (
                    <div
                      className={`mt-3 flex items-start gap-2 text-sm ${testResult.ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-600 dark:text-red-300"}`}
                    >
                      {testResult.ok ? (
                        <CircleCheck className="mt-0.5 shrink-0" size={16} />
                      ) : (
                        <CircleX className="mt-0.5 shrink-0" size={16} />
                      )}
                      <span>
                        {testResult.message}
                        {testResult.serverName
                          ? ` ${testResult.serverName}${testResult.serverVersion ? ` ${testResult.serverVersion}` : ""}`
                          : ""}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <Toggle
                    disabled={!canManage}
                    checked={draft.enabled}
                    onChange={(event) =>
                      setDraft({ ...draft, enabled: event.target.checked })
                    }
                  />{" "}
                  {t("Available in chat")}
                </label>
                {canManage && organizationOnly ? (
                  <span className="text-sm text-stone-500">
                    {t("Organization")}
                  </span>
                ) : canManage ? (
                  <label className="flex items-center gap-2 text-sm">
                    <span>{t("Visibility")}</span>
                    <select
                      className="field w-auto py-2"
                      value={draft.scope}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          scope: event.target.value as McpDraft["scope"],
                        })
                      }
                    >
                      {!canManagePlatform && (
                        <option value="personal">{t("Only me")}</option>
                      )}
                      {canManagePlatform && (
                        <option value="platform">{t("Everyone")}</option>
                      )}
                    </select>
                  </label>
                ) : null}
                <div className="ml-auto flex gap-2">
                  {canManage ? (
                    <>
                      {selected && (
                        <button
                          className="button text-red-600"
                          onClick={() => void remove()}
                        >
                          <Trash2 size={16} /> {t("Delete")}
                        </button>
                      )}
                      <button
                        className="button-primary"
                        disabled={
                          saving ||
                          !draft.name ||
                          !validToolPrefix ||
                          !draft.endpointUrl ||
                          (draft.authType === "oauth" &&
                            !draft.oauthClientId.trim())
                        }
                        onClick={() => void save()}
                      >
                        <Save size={16} /> {t("Save")}
                      </button>
                    </>
                  ) : selected &&
                    draft.authScope === "user" &&
                    draft.authType !== "none" &&
                    draft.authType !== "oauth" ? (
                    <button
                      className="button-primary"
                      disabled={saving || !draft.config.trim()}
                      onClick={() => void saveMyCredentials()}
                    >
                      <Save size={16} /> {t("Save my credentials")}
                    </button>
                  ) : null}
                </div>
              </div>
              {error && (
                <pre className="rounded-xl bg-red-50 p-3 text-xs leading-5 whitespace-pre-wrap text-red-700 dark:bg-red-950/30 dark:text-red-300">
                  {error}
                </pre>
              )}
            </>
          )}
        </div>
      </div>
    </SettingsShell>
  );
}
